var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useState, useRef, useCallback } from 'react';
import classNames from 'classnames';
import useTaroBaseEvents from './useTaroBaseEvents';
function useTaroHoverableEvents(_a, defaultHoverClass) {
    var { className, hoverClass, hoverStopPropagation = false, hoverStartTime = 50, hoverStayTime = 400 } = _a, baseProps = __rest(_a, ["className", "hoverClass", "hoverStopPropagation", "hoverStartTime", "hoverStayTime"]);
    const _b = useTaroBaseEvents(baseProps), { onMouseDown, onTouchStart, onMouseUp, onTouchEnd, onMouseLeave, onTouchCancel } = _b, rest = __rest(_b, ["onMouseDown", "onTouchStart", "onMouseUp", "onTouchEnd", "onMouseLeave", "onTouchCancel"]);
    const [hovered, setHovered] = useState(false);
    const timer = useRef(null);
    if (typeof hoverClass === 'undefined') {
        hoverClass = defaultHoverClass;
    }
    const handleStart = useCallback((reactEvent) => {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        if (reactEvent.currentTarget === reactEvent.target) {
            reactEvent.stopHoverClass = false;
        }
        if (hoverClass !== 'none' && !reactEvent.stopHoverClass) {
            timer.current = setTimeout(() => {
                setHovered(true);
            }, hoverStartTime);
        }
        if (hoverStopPropagation) {
            reactEvent.stopHoverClass = true;
        }
    }, [hoverClass, hoverStartTime, hoverStopPropagation]);
    const handleEnd = useCallback(() => {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
            setHovered(false);
        }, hoverStayTime);
    }, [hoverStayTime]);
    const handleMouseDown = useCallback((reactEvent) => {
        if (!('ontouchstart' in window)) {
            handleStart(reactEvent);
        }
        onMouseDown(reactEvent);
    }, [handleStart, onMouseDown]);
    const handleTouchStart = useCallback((reactEvent) => {
        handleStart(reactEvent);
        onTouchStart(reactEvent);
    }, [handleStart, onTouchStart]);
    const handleMouseUp = useCallback((reactEvent) => {
        if (!('ontouchend' in window)) {
            handleEnd();
        }
        onMouseUp(reactEvent);
    }, [handleEnd, onMouseUp]);
    const handleTouchEnd = useCallback((reactEvent) => {
        handleEnd();
        onTouchEnd(reactEvent);
    }, [handleEnd, onTouchEnd]);
    const handleMouseLeave = useCallback((reactEvent) => {
        if (!('ontouchcancel' in window)) {
            handleEnd();
        }
        onMouseLeave(reactEvent);
    }, [handleEnd, onMouseLeave]);
    const handleTouchCancel = useCallback((reactEvent) => {
        handleEnd();
        onTouchCancel(reactEvent);
    }, [handleEnd, onTouchCancel]);
    return Object.assign({ className: classNames({
            [hoverClass]: hoverClass && hoverClass !== 'none' && hovered
        }, className), onMouseDown: handleMouseDown, onTouchStart: handleTouchStart, onMouseUp: handleMouseUp, onTouchEnd: handleTouchEnd, onMouseLeave: handleMouseLeave, onTouchCancel: handleTouchCancel }, rest);
}
export default useTaroHoverableEvents;
